// Navbar.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import logo from '../images/logo.png';

const Nav = styled.nav`
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem calc((100vw - 1200px) / 2);
  z-index: 20;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  position: sticky;
  top: 0;

  @media (max-width: 1024px) {
    padding: 0.5rem calc((100vw - 900px) / 2);
  }
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  z-index: 30;
`;

const Logo = styled.img`
  height: 40px;
`;

const NavMenu = styled.div`
  display: flex;
  align-items: center;
  margin-right: -24px;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #ffffff;
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0 1rem;
  height: 100%;
  cursor: pointer;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    background: rgb(54, 136, 243);
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    transition: width 0.3s;
  }

  &:hover {
    color: rgb(72, 163, 243);
    &:after {
      width: 70%;
    }
  }
`;

const NavBtn = styled.div`
  display: flex;
  align-items: center;
  margin-right: 24px;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const NavBtnLink = styled.a`
  border-radius: 4px;
  background: rgba(19, 49, 221, 1);
  padding: 10px 22px;
  color: #fff;
  cursor: pointer;
  text-decoration: none;
  font-family: 'Roboto', sans-serif;
  font-weight: bold;

  &:hover {
    background: #58a9c4e;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownContent = styled.div`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  position: absolute;
  background-color: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(10px);
  min-width: 200px;
  box-shadow: 0px 8px 16px rgba(0,0,0,0.2);
  z-index: 1;
  border-radius: 4px;
  top: 30px;
  left: 0;

  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    background-color: transparent;
    padding-left: 1rem;
  }
`;

const DropdownLink = styled(Link)`
  color: #ffffff;
  padding: 12px 16px;
  text-decoration: none;
  display: block;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;

  &:hover {
    background-color: #333;
    color: #4bdfecff;
  }

  @media (max-width: 768px) {
    padding: 8px 0;
    background-color: transparent;
  }
`;

const MobileIcon = styled.div`
  display: none;
  z-index: 30;
  cursor: pointer;

  @media screen and (max-width: 768px) {
    display: block;
    color: white;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: ${({ isOpen }) => (isOpen ? '0' : '-100%')};
  width: 250px;
  height: 100%;
  background-color: rgba(0,0,0,0.95);
  backdrop-filter: blur(8px);
  z-index: 25;
  display: flex;
  flex-direction: column;
  padding-top: 80px;
  transition: 0.3s ease-in-out;
`;

const MobileNavLink = styled(Link)`
  color: #fff;
  padding: 12px 20px;
  text-decoration: none;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;

  &:hover {
    background-color: #333;
    color: #FF6B35;
  }
`;

const MobileDropdownToggle = styled.div`
  color: #fff;
  padding: 12px 20px;
  cursor: pointer;
  font-weight: 500;
  &:hover {
    background-color: #333;
    color: rgb(15, 9, 108);
  }
`;

const NavItem = styled.div`
  position: relative;
`;

const NavMain = styled.span`
  color: #fff;
  cursor: pointer;
  font-weight: 500;
  padding: 8px 12px;

  &:hover {
    color: #ff6b35;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 120%;
  left: 0;
  background: #111;
  border-radius: 8px;
  min-width: 160px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.4);
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;

  ${NavItem}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;


const Navbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
    setMobileDropdown(null);
  };

  const toggleMobileDropdown = (name) => {
    setMobileDropdown(mobileDropdown === name ? null : name);
  };

  const handleMouseEnter = (name) => setActiveDropdown(name);
  const handleMouseLeave = () => setActiveDropdown(null);
  const toggleDesktopDropdown = (name) =>
    setActiveDropdown((prev) => (prev === name ? null : name));

  return (
    <>
      <Nav>
        <LogoContainer>
          <Logo src={logo} alt="Company Logo" />
        </LogoContainer>

        {/* Desktop Menu */}
        <NavMenu>
          <NavLink to="/">Home</NavLink>
          <NavItem>
            <NavMain>About ▼</NavMain>

            <Dropdown>
              <DropdownLink to="/company">About Us</DropdownLink>
              <DropdownLink to="/leadership-message">Director Messaage</DropdownLink>
            </Dropdown>
          </NavItem>
          <DropdownContainer
            onMouseEnter={() => handleMouseEnter('services')}
            onMouseLeave={handleMouseLeave}
          >
            <NavLink to="/services">Services ▼</NavLink>
            <DropdownContent isOpen={activeDropdown === 'services'}>
              <DropdownLink to="/services/android-zero-touch-enrollment">Android Zero Touch Enrollment</DropdownLink>
              <DropdownLink to="/services/android-management">Android Management</DropdownLink>
              <DropdownLink to="/services/cloud-computing">Cloud Computing</DropdownLink>
              <DropdownLink to="/services/cloud-management-service">Cloud Management Service</DropdownLink>
              <DropdownLink to="/services/cyber-security">Cyber Security</DropdownLink>
              <DropdownLink to="/services/ios-management">iOS Management</DropdownLink>
              <DropdownLink to="/services/mobile-management">Mobile Management</DropdownLink>
              <DropdownLink to="/services/it-management">IT Management</DropdownLink>
              <DropdownLink to="/services/it-support">IT Support</DropdownLink>
              <DropdownLink to="/services/software-development">Software Development</DropdownLink>
              <DropdownLink to="/services/digital-marketing">Digital Marketing</DropdownLink>
            </DropdownContent>
          </DropdownContainer>

          {/* INDUSTRIES */}
          <DropdownContainer
            onMouseEnter={() => handleMouseEnter('industries')}
            onMouseLeave={handleMouseLeave}
          >
            <NavLink
              as="div"
              role="button"
              tabIndex={0}
              onClick={() => toggleDesktopDropdown('industries')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleDesktopDropdown('industries');
                }
              }}
            >
              Industries ▼
            </NavLink>
            <DropdownContent isOpen={activeDropdown === 'industries'}>
              <DropdownLink to="/industries/accounting-finance">Accounting & Finance</DropdownLink>
              <DropdownLink to="/industries/education">Education</DropdownLink>
              <DropdownLink to="/industries/energy-utilities">Energy & Utilities</DropdownLink>
              <DropdownLink to="/industries/government-public">Government & Public</DropdownLink>
              <DropdownLink to="/industries/legal-law-firm">Legal, Law Firm</DropdownLink>
              <DropdownLink to="/industries/manufacturing">Manufacturing</DropdownLink>
            </DropdownContent>
          </DropdownContainer>

          <DropdownContainer
            onMouseEnter={() => toggleDesktopDropdown('upcoming-project')}
            onMouseLeave={() => toggleDesktopDropdown(null)}
          >
            <NavLink
              as="div"
              role="button"
              tabIndex={0}
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'upcoming-project'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleDesktopDropdown('upcoming-project');
                }
              }}
            >
              Upcoming Project ▼
            </NavLink>

            <DropdownContent isOpen={activeDropdown === 'upcoming-project'}>
              <DropdownLink to="/upcoming-project/smart-watch-mdm">
                Smart Watch with MDM
              </DropdownLink>

              <DropdownLink to="/upcoming-project/finance-approved-tablet-phone">
                Finance Approved Tablet Phone
              </DropdownLink>

              <DropdownLink to="/upcoming-project/vr-glasses">
                VR Glasses
              </DropdownLink>

              <DropdownLink to="/upcoming-project/ml-module">
                ML Module
              </DropdownLink>
            </DropdownContent>
          </DropdownContainer>



          <NavLink to="/portfolio">Portfolio</NavLink>

<a href="https://zyntro.in/blog/" className="nav-link">
    Blog
  </a>
            <NavLink to="/contact">Contact Us</NavLink>
        </NavMenu>

        {/* Desktop CTA Button → Call */}
        <NavBtn>
          <NavBtnLink href="tel:+918787044997">Consultant</NavBtnLink>
        </NavBtn>

        {/* Mobile Icon */}
        <MobileIcon onClick={toggleMobileMenu}>
          {mobileOpen ? <X size={28} /> : <Menu size={28} />}
        </MobileIcon>
      </Nav>

      {/* Mobile Slide Menu */}
      <MobileMenu isOpen={mobileOpen}>
        <MobileNavLink to="/" onClick={toggleMobileMenu}>Home</MobileNavLink>
        <MobileDropdownToggle onClick={() => toggleMobileDropdown('about')}>
          About ▼
        </MobileDropdownToggle>

        {mobileDropdown === 'about' && (
          <>
            <DropdownLink to="/company" onClick={toggleMobileMenu}>
              About Us
            </DropdownLink>

            <DropdownLink to="/leadership-message" onClick={toggleMobileMenu}>
              Director Message
            </DropdownLink>
          </>
        )}

        <MobileDropdownToggle onClick={() => toggleMobileDropdown('services')}>
          Services ▼
        </MobileDropdownToggle>
        {mobileDropdown === 'services' && (
          <>
            <DropdownLink to="/services/android-zero-touch-enrollment" onClick={toggleMobileMenu}>Android Zero Touch Enrollment</DropdownLink>
            <DropdownLink to="/services/android-management" onClick={toggleMobileMenu}>Android Management</DropdownLink>
            <DropdownLink to="/services/cloud-computing" onClick={toggleMobileMenu}>Cloud Computing</DropdownLink>
            <DropdownLink to="/services/cloud-management-service" onClick={toggleMobileMenu}>Cloud Management service</DropdownLink>
            <DropdownLink to="/services/cyber-security" onClick={toggleMobileMenu}>Cyber Security</DropdownLink>
            <DropdownLink to="/services/ios-management" onClick={toggleMobileMenu}>iOS Management</DropdownLink>
            <DropdownLink to="/services/mobile-management" onClick={toggleMobileMenu}>Mobile Management</DropdownLink>
            <DropdownLink to="/services/it-management" onClick={toggleMobileMenu}>IT Management</DropdownLink>
            <DropdownLink to="/services/it-support" onClick={toggleMobileMenu}>IT Support</DropdownLink>
            <DropdownLink to="/services/software-development" onClick={toggleMobileMenu}>Software Development</DropdownLink>
            <DropdownLink to="/services/digital-marketing" onClick={toggleMobileMenu}>Digital Marketing</DropdownLink>
          </>
        )}

        <MobileDropdownToggle onClick={() => toggleMobileDropdown('industries')}>
          Industries ▼
        </MobileDropdownToggle>
        {mobileDropdown === 'industries' && (
          <>
            <DropdownLink to="/industries/accounting-finance" onClick={toggleMobileMenu}>Accounting & Finance</DropdownLink>
            <DropdownLink to="/industries/education" onClick={toggleMobileMenu}>Education</DropdownLink>
            <DropdownLink to="/industries/energy-utilities" onClick={toggleMobileMenu}>Energy & Utilities</DropdownLink>
            <DropdownLink to="/industries/government-public" onClick={toggleMobileMenu}>Government & Public</DropdownLink>
            <DropdownLink to="/industries/legal-law-firm" onClick={toggleMobileMenu}>Legal, Law Firm</DropdownLink>
            <DropdownLink to="/industries/manufacturing" onClick={toggleMobileMenu}>Manufacturing</DropdownLink>
          </>
        )}

        <MobileDropdownToggle onClick={() => toggleMobileDropdown('upcoming-project')}>
          Upcoming Project ▼
        </MobileDropdownToggle>

        {mobileDropdown === 'upcoming-project' && (
          <>
            <DropdownLink
              to="/upcoming-project/smart-watch-mdm"
              onClick={toggleMobileMenu}
            >
              Smart Watch with MDM
            </DropdownLink>

            <DropdownLink
              to="/upcoming-project/finance-approved-tablet-phone"
              onClick={toggleMobileMenu}
            >
              Finance Approved Tablet Phone
            </DropdownLink>

            <DropdownLink
              to="/upcoming-project/vr-glasses"
              onClick={toggleMobileMenu}
            >
              VR Glasses
            </DropdownLink>

            <DropdownLink
              to="/upcoming-project/ml-module"
              onClick={toggleMobileMenu}
            >
              ML Module
            </DropdownLink>
          </>
        )}



        <MobileNavLink to="/portfolio" onClick={toggleMobileMenu}>Portfolio</MobileNavLink>

        <MobileNavLink to="/blog" onClick={toggleMobileMenu}>Blog</MobileNavLink>
        <MobileNavLink to="/contact" onClick={toggleMobileMenu}>Contact Us</MobileNavLink>

        {/* Mobile CTA Button → Call */}
        <MobileNavLink as="a" href="tel:+918787044997" onClick={toggleMobileMenu}>
          Free Consultant
        </MobileNavLink>
      </MobileMenu>
    </>
  );
};

export default Navbar;
